import type { LucideIcon } from "lucide-react"
import {
  ArrowRight,
  CircleCheck,
  Clock,
  Coins,
  Info,
  Route,
  TriangleAlert,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { getIntlLocale } from "@/lib/i18n/format"
import { Separator } from "@/components/ui/separator"

/** One end of the order — country + postal code are what matters at a glance,
 * city is optional context. */
export type TaskStop = {
  /** ISO 3166-1 alpha-2 country code, e.g. "PL", "DE". */
  countryCode: string
  /** Postal / ZIP code. */
  postalCode: string
  /** City / locality — shown under the code when present. */
  city?: string
}

/** Colours the additional-info note. */
export type TaskNoteTone = "positive" | "warning" | "neutral"

/** A document attached to the order — a scan the dispatcher can open. */
export type TaskContentDocument = {
  fileName: string
  onOpen: () => void
}

/**
 * The "order at a glance" body — pickup → delivery, how long it takes, how far
 * it runs and its tolls (each against plan), optionally its documents and a
 * line on how it went. Shared by the `WidgetTaskSummary` card in the Agent
 * thread and the finished-task section of the `TaskDetailsTool`, so both read
 * the same way.
 */
export type TaskSummaryContentProps = {
  pickup: TaskStop
  delivery: TaskStop
  durationMinutes: number
  distanceKm: number
  tollsEur: number
  durationDeltaMinutes?: number
  distanceDeltaKm?: number
  tollsDeltaEur?: number
  /** Documents attached to the order — shown between the values and the note.
   * Omit where the surrounding view lists documents itself. */
  documents?: TaskContentDocument[]
  note?: string
  noteTone?: TaskNoteTone
  className?: string
}

const formatDuration = (minutes: number): string => {
  const total = Math.max(0, Math.round(minutes))
  const h = Math.floor(total / 60)
  const m = total % 60
  if (h === 0) return `${m} min`
  if (m === 0) return `${h} h`
  return `${h} h ${m} min`
}

/** A signed delta vs. plan. Lower is always better here (shorter / cheaper),
 * so a negative value reads as green and a positive one as red. */
const deltaText = (
  value: number,
  unit: string,
  nf: Intl.NumberFormat,
): string => {
  const rounded = Math.round(value)
  if (rounded === 0) return ""
  const sign = rounded > 0 ? "+" : "-"
  const magnitude =
    unit === "min"
      ? formatDuration(Math.abs(rounded))
      : `${nf.format(Math.abs(rounded))} ${unit}`
  return `${sign}${magnitude}`
}

/** Regional-indicator flag for an alpha-2 country code. */
const flagEmoji = (countryCode: string): string => {
  const cc = countryCode.trim().toUpperCase()
  if (!/^[A-Z]{2}$/.test(cc)) return "🏳️"
  return String.fromCodePoint(
    ...Array.from(cc, (ch) => 0x1f1e6 + ch.charCodeAt(0) - 65),
  )
}

const CircleFlag = ({ countryCode }: { countryCode: string }) => (
  <span
    className="grid h-4 w-4 shrink-0 place-items-center overflow-hidden rounded-full ring-1 ring-inset ring-black/10 dark:ring-white/15"
    title={countryCode.toUpperCase()}
    aria-hidden
  >
    <span
      className="text-[13px] leading-none"
      style={{ transform: "scale(1.4)" }}
    >
      {flagEmoji(countryCode)}
    </span>
  </span>
)

const Stop = ({ stop }: { stop: TaskStop }) => (
  <span className="flex min-w-0 items-center gap-1.5 text-sm font-semibold text-foreground">
    <CircleFlag countryCode={stop.countryCode} />
    <span className="truncate">
      {stop.countryCode} {stop.postalCode}
    </span>
  </span>
)

const NOTE_TONE: Record<TaskNoteTone, { icon: LucideIcon; className: string }> =
  {
    positive: {
      icon: CircleCheck,
      className: "text-green-700 dark:text-green-400",
    },
    warning: {
      icon: TriangleAlert,
      className: "text-amber-700 dark:text-amber-400",
    },
    neutral: { icon: Info, className: "text-muted-foreground" },
  }

const Stat = ({
  icon: Icon,
  label,
  value,
  delta,
}: {
  icon: LucideIcon
  label: string
  value: string
  /** Signed delta vs. plan; "+" reads red, "-" reads green. */
  delta?: string
}) => (
  <div className="flex min-w-0 flex-1 flex-col gap-1">
    <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="truncate">{label}</span>
    </span>
    <span className="flex min-w-0 flex-col gap-0">
      <span className="truncate text-base font-semibold text-foreground">
        {value}
      </span>
      {delta && (
        <span
          className={cn(
            "truncate text-sm font-medium",
            delta.startsWith("+")
              ? "text-red-600 dark:text-red-400"
              : "text-emerald-700 dark:text-emerald-400",
          )}
        >
          {delta}
        </span>
      )}
    </span>
  </div>
)

export const TaskSummaryContent = ({
  pickup,
  delivery,
  durationMinutes,
  distanceKm,
  tollsEur,
  durationDeltaMinutes,
  distanceDeltaKm,
  tollsDeltaEur,
  documents,
  note,
  noteTone = "neutral",
  className,
}: TaskSummaryContentProps) => {
  const { t } = useTranslation()
  const nf = new Intl.NumberFormat(getIntlLocale("pl-PL"))
  const tone = NOTE_TONE[noteTone]
  const NoteIcon = tone.icon
  const hasDocuments = !!documents && documents.length > 0

  return (
    <div className={cn("space-y-3", className)}>
      {/* Pickup → delivery — flag + country + postal code, city as context. */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-x-3 gap-y-1">
        <Stop stop={pickup} />
        <ArrowRight
          className="h-4 w-4 shrink-0 text-muted-foreground"
          aria-hidden
        />
        <Stop stop={delivery} />

        {(pickup.city || delivery.city) && (
          <>
            <span className="truncate text-sm text-muted-foreground">
              {pickup.city}
            </span>
            <span aria-hidden />
            <span className="truncate text-sm text-muted-foreground">
              {delivery.city}
            </span>
          </>
        )}
      </div>

      <Separator />

      <div className="flex items-stretch gap-3">
        <Stat
          icon={Clock}
          label={t("chat.taskSummary.time")}
          value={formatDuration(durationMinutes)}
          delta={
            durationDeltaMinutes != null
              ? deltaText(durationDeltaMinutes, "min", nf)
              : undefined
          }
        />
        <Separator orientation="vertical" />
        <Stat
          icon={Route}
          label={t("chat.taskSummary.distance")}
          value={`${nf.format(Math.round(distanceKm))} km`}
          delta={
            distanceDeltaKm != null
              ? deltaText(distanceDeltaKm, "km", nf)
              : undefined
          }
        />
        <Separator orientation="vertical" />
        <Stat
          icon={Coins}
          label={t("chat.taskSummary.tolls")}
          value={`${nf.format(Math.round(tollsEur))} €`}
          delta={
            tollsDeltaEur != null
              ? deltaText(tollsDeltaEur, "€", nf)
              : undefined
          }
        />
      </div>

      {hasDocuments && (
        <>
          <Separator />
          <ul className="space-y-2">
            {documents!.map((doc, i) => (
              <li key={i}>
                <button
                  type="button"
                  onClick={doc.onOpen}
                  className="block w-full truncate rounded-sm text-left text-sm text-base-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {doc.fileName}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {note && (
        <>
          {hasDocuments && <Separator />}
          <div className={cn("flex items-start gap-2 text-sm", tone.className)}>
            <NoteIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span className="min-w-0">{note}</span>
          </div>
        </>
      )}
    </div>
  )
}

export default TaskSummaryContent
